import { jsx as _jsx } from "react/jsx-runtime";
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import MapObjectPopup from '../components/MapObjectPopup';
import { MarkerCreationPopup } from '../components/MarkerCreationPopup';
import { StructureTypeSelector } from '../components/StructureTypeSelector';
import { StructureForm } from '../components/StructureForm';
import UprnDatasetPopup from '../components/UprnDatasetPopup';
import { DialogProvider } from '../context/DialogContext';
// Cache for React roots to avoid memory leaks
const popupRoots = new WeakMap();
/**
 * Renders a modern React-based popup for map objects
 * Called via makeMapPopup or directly by CFN.renderModernPopup
 */
export function renderMapObjectPopup(feature, context) {
    console.log('[popupRenderer] renderMapObjectPopup called with feature:', feature);
    console.log('[popupRenderer] context:', context);
    // Create container element
    const container = document.createElement('div');
    container.className = 'map-object-popup-container';
    // Create React root
    const root = createRoot(container);
    popupRoots.set(container, root);
    // Render the component wrapped in DialogProvider
    // Note: UploadQueueProvider is NOT needed here - popups use addFilesToQueue directly
    // and the main app's useUploadQueue handles processing
    // Use flushSync to ensure React renders synchronously so DOM elements
    // (like #startPath, #endPath buttons) exist when external code attaches handlers
    flushSync(() => {
        root.render(_jsx(DialogProvider, { children: _jsx(MapObjectPopup, { feature: feature, context: context, onClose: () => {
                    // Clean up when popup closes
                    const cachedRoot = popupRoots.get(container);
                    if (cachedRoot) {
                        cachedRoot.unmount();
                        popupRoots.delete(container);
                    }
                } }) }));
    });
    console.log('[popupRenderer] Returning container element');
    return container;
}
/**
 * Renders a modern React-based popup for marker creation (right-click)
 * This replaces the legacy Bootstrap popup with consistent design
 */
export function renderMarkerCreationPopup(coordinates, callbacks) {
    // Create container element
    const container = document.createElement('div');
    container.className = 'marker-creation-popup-container';
    // Create React root
    const root = createRoot(container);
    popupRoots.set(container, root);
    // Render the component
    root.render(_jsx(MarkerCreationPopup, { coordinates: coordinates, onCreateStructure: callbacks.onCreateStructure, onCreatePoi: callbacks.onCreatePoi, onRemove: callbacks.onRemove, onClose: () => {
            // Clean up when popup closes
            const cachedRoot = popupRoots.get(container);
            if (cachedRoot) {
                cachedRoot.unmount();
                popupRoots.delete(container);
            }
        } }));
    return container;
}
/**
 * Renders a modern React-based structure type selector
 * This replaces the legacy Bootstrap structure type selection popup
 */
export function renderStructureTypeSelector(callbacks) {
    // Create container element
    const container = document.createElement('div');
    container.className = 'structure-type-selector-container';
    // Create React root
    const root = createRoot(container);
    popupRoots.set(container, root);
    // Render the component
    root.render(_jsx(StructureTypeSelector, { onSelectType: callbacks.onSelectType, onBack: callbacks.onBack, onRemove: callbacks.onRemove, onClose: () => {
            // Clean up when popup closes
            const cachedRoot = popupRoots.get(container);
            if (cachedRoot) {
                cachedRoot.unmount();
                popupRoots.delete(container);
            }
        } }));
    return container;
}
/**
 * Renders a modern React-based structure form
 * This replaces the legacy Bootstrap form generation
 */
export function renderStructureForm(formData, formType, gridCoords, callbacks) {
    // Create container element
    const container = document.createElement('div');
    container.className = 'structure-form-container';
    // Create React root
    const root = createRoot(container);
    popupRoots.set(container, root);
    // Render the component
    root.render(_jsx(StructureForm, { formData: formData, formType: formType, gridCoords: gridCoords, onSuccess: callbacks.onSuccess, onError: callbacks.onError, onBack: callbacks.onBack, onRemove: callbacks.onRemove }));
    return container;
}
/**
 * Renders a modern React-based popup for UPRN dataset features
 * This replaces the legacy inline HTML popup with a theme-aware design
 */
export function renderUprnDatasetPopup(feature, onClose) {
    // Create container element
    const container = document.createElement('div');
    container.className = 'uprn-dataset-popup-container';
    // Create React root
    const root = createRoot(container);
    popupRoots.set(container, root);
    // Render the component
    root.render(_jsx(UprnDatasetPopup, { feature: feature, onClose: () => {
            // Clean up when popup closes
            const cachedRoot = popupRoots.get(container);
            if (cachedRoot) {
                cachedRoot.unmount();
                popupRoots.delete(container);
            }
            if (onClose)
                onClose();
        } }));
    return container;
}
/**
 * Clean up a popup when it's removed from the DOM
 */
export function cleanupMapObjectPopup(container) {
    const root = popupRoots.get(container);
    if (root) {
        root.unmount();
        popupRoots.delete(container);
    }
}
/**
 * Ensures the popup is fully visible on screen by panning the map if necessary.
 * This is called after React content has rendered to account for dynamic content.
 */
function ensurePopupVisible(popup, map) {
    if (!popup || !popup._container || !map)
        return;
    const container = popup._container;
    const mapContainer = map.getContainer();
    const mapRect = mapContainer.getBoundingClientRect();
    const popupRect = container.getBoundingClientRect();
    // Calculate padding (space to leave between popup and map edge)
    const padding = 20;
    // Calculate how much the popup extends beyond each edge
    let dx = 0;
    let dy = 0;
    // Check right edge
    if (popupRect.right > mapRect.right - padding) {
        dx = popupRect.right - mapRect.right + padding;
    }
    // Check left edge
    if (popupRect.left < mapRect.left + padding) {
        dx = popupRect.left - mapRect.left - padding;
    }
    // Check bottom edge
    if (popupRect.bottom > mapRect.bottom - padding) {
        dy = popupRect.bottom - mapRect.bottom + padding;
    }
    // Check top edge
    if (popupRect.top < mapRect.top + padding) {
        dy = popupRect.top - mapRect.top - padding;
    }
    // Pan the map if needed
    if (dx !== 0 || dy !== 0) {
        map.panBy([dx, dy], { animate: true, duration: 0.25 });
    }
}
/**
 * Get a human-readable label for a feature (for tab display)
 */
function getFeatureTabLabel(feature) {
    const props = feature?.properties || {};
    // Check layer type first - PIA layers
    if (props.layer === 'cfn:pia_structure' || props.layer === 'cfn:phys_node')
        return 'Structure';
    if (props.layer === 'cfn:pia_duct')
        return 'Conduit';
    if (props.LAYER === 'BFT' || props.layer === 'cfn:bft')
        return 'BFT';
    if (props.LAYER === 'LEADIN' || props.layer === 'cfn:leadinRoutes' || props.layer === 'cfn:inferredLeadIns')
        return 'Lead-in';
    // Try to infer from feature properties - conduits/ducts
    // TYPE_NAME can be CONDUIT, TRENCH, etc. - all are duct infrastructure
    if (props.TYPE_NAME === 'CONDUIT' || props.TYPE_NAME === 'TRENCH' ||
        props.PLANT_ITEM === 'DUCT' || props.PLANT_ITEM === 'D54' ||
        props.OBJECT_CLASS === 'DS' || props.SECTION_NUMBER ||
        props.DUCT_ID || props.duct_id)
        return 'Conduit';
    // Try common name fields
    if (props.Name)
        return String(props.Name).substring(0, 15);
    if (props.name)
        return String(props.name).substring(0, 15);
    if (props.layer_name)
        return String(props.layer_name).substring(0, 15);
    if (props.TYPE)
        return String(props.TYPE).substring(0, 15);
    if (props.type)
        return String(props.type).substring(0, 15);
    // Try to infer from feature type
    if (props.POLE_NO || props.pole_no)
        return 'Pole';
    if (props.STRUCTURE_ID || props.structure_id || props.STRUCTURE_NAME || props.STRUCTURE_NUMBER)
        return 'Structure';
    if (props.UPRN || props.uprn)
        return 'Property';
    return 'Feature';
}
/**
 * Create and open a map popup for a selected feature
 * This replaces the legacy makeModernPopup from select-object.js
 *
 * @param feature - The GeoJSON feature to show popup for
 * @param context - Context including click event, layer, map, and clickId
 * @returns The created popup, or null if another handler won arbitration
 */
export function makeMapPopup(feature, context) {
    // Import getPopupLocation dynamically to avoid circular dependencies
    const { getPopupLocation } = require('../../../utils/mapSelection');
    const map = context.map;
    const clickLatLng = context.event?.latlng;
    if (!map || !clickLatLng) {
        console.warn('[makeMapPopup] Missing map or click latlng');
        return null;
    }
    // Clear old inputs (if any)
    document.getElementById('CameraInput')?.remove();
    document.getElementById('UploadAssets')?.remove();
    document.getElementById('OpenGallery')?.remove();
    document.getElementById('startPath')?.remove();
    document.getElementById('endPath')?.remove();
    document.getElementById('SyncStructure')?.remove();
    // Calculate popup location
    const popupLocation = getPopupLocation(feature, clickLatLng);
    const popupLatLng = L.latLng(popupLocation[1], popupLocation[0]);
    // If coordinator is available and we have a clickId, use it for arbitration
    const popupCoordinator = window.popupCoordinator;
    if (popupCoordinator && context.clickId) {
        // Render the React popup content
        const popupContainer = renderMapObjectPopup(feature, context);
        // Get a tab label for this feature
        const tabLabel = getFeatureTabLabel(feature);
        const won = popupCoordinator.submitResult({
            clickId: context.clickId,
            clickLatLng: clickLatLng,
            feature,
            featureLatLng: popupLatLng,
            map,
            source: context.source || 'crosshair',
            createPopup: () => createPopupInternal(feature, context, popupLocation, map),
            // Pass tab info to participate in batched rendering
            tabLabel,
            popupContent: popupContainer,
        });
        if (!won) {
            // Another handler had a closer result
            return null;
        }
        return context.popup;
    }
    // No coordinator or no clickId - create popup directly
    return createPopupInternal(feature, context, popupLocation, map);
}
/**
 * Internal function to create the popup
 * Separated for use with PopupCoordinator
 */
function createPopupInternal(feature, context, popupLocation, map) {
    // Store current object in CFN.selectObject if available
    const CFN = window.CFN;
    if (CFN?.selectObject) {
        CFN.selectObject.currentObject = feature;
    }
    // Add map to context for React components
    context.map = map;
    // Render the modern React popup
    const popupContainer = renderMapObjectPopup(feature, context);
    // Create Leaflet popup with the React container
    context.popup = L.popup({
        minWidth: 240,
        maxWidth: 300,
        className: 'modern-map-popup',
        autoPan: false, // Disable initial autopan - we'll do it manually after React renders
    })
        .setContent(popupContainer)
        .setLatLng({ lng: popupLocation[0], lat: popupLocation[1] })
        .openOn(map);
    // Push history state for back button navigation
    window.history.pushState({ mapPopupOpen: true }, '');
    let historyPushed = true;
    let closedViaBackButton = false;
    // Listen for back button to close popup
    const handlePopState = (event) => {
        // Don't close if a child dialog (gallery/lightbox) is still open
        if (event.state?.galleryOpen || event.state?.lightboxOpen) {
            return;
        }
        if (!event.state?.mapPopupOpen && context.popup) {
            closedViaBackButton = true;
            map.closePopup(context.popup);
        }
    };
    window.addEventListener('popstate', handlePopState);
    // Clean up listener when popup closes
    context.popup.on('remove', () => {
        window.removeEventListener('popstate', handlePopState);
        // If we pushed a history state and this isn't being closed via the back button,
        // we need to pop the history state to clean up
        if (historyPushed && !closedViaBackButton) {
            historyPushed = false;
            // Check if current state has our popup flag before going back
            if (window.history.state?.mapPopupOpen) {
                window.history.back();
            }
        }
    });
    // Trigger autopan after React content has rendered
    setTimeout(() => {
        requestAnimationFrame(() => {
            ensurePopupVisible(context.popup, map);
        });
        // Attach route planning button handlers (React renders these with IDs but no onClick)
        const startPath = document.querySelector('#startPath');
        const endPath = document.querySelector('#endPath');
        if (startPath) {
            startPath.addEventListener('click', () => {
                // Use routing markers for path finding
                if (CFN?.routingMarkers?.setStartPoint) {
                    CFN.routingMarkers.setStartPoint(context.event.latlng);
                }
                map.closePopup();
            });
        }
        if (endPath) {
            endPath.addEventListener('click', () => {
                // Use routing markers for path finding
                if (CFN?.routingMarkers?.setEndPoint) {
                    CFN.routingMarkers.setEndPoint(context.event.latlng);
                }
                map.closePopup();
            });
        }
    }, 0);
    // Cleanup React component when popup closes
    context.popup.on('remove', () => {
        if (typeof CFN?.cleanupModernPopup === 'function') {
            CFN.cleanupModernPopup(popupContainer);
        }
    });
    return context.popup;
}
/**
 * Bind a click handler to a Leaflet layer that shows a popup via CFN.makePopup
 * Use this for GeoJSON layers instead of bindPopup() to get route buttons support
 *
 * @param layer - The Leaflet layer to bind the click handler to
 * @param feature - The GeoJSON feature
 * @param map - The Leaflet map instance
 */
export function bindFeaturePopup(layer, feature, map) {
    layer.on('click', (e) => {
        // Register click with coordinator
        const popupCoordinator = window.popupCoordinator;
        const clickId = popupCoordinator?.registerClick(e.latlng);
        if (popupCoordinator && clickId) {
            popupCoordinator.expectSources(clickId, ['crosshair']);
        }
        // Create context for popup
        const context = {
            layer,
            event: { latlng: e.latlng },
            clickId,
            map,
        };
        // Use makeMapPopup
        makeMapPopup(feature, context);
        // Stop propagation to prevent duplicate handling by map click
        L.DomEvent.stopPropagation(e);
    });
}
/**
 * Initialize the popup renderer system
 * Call this once when the map initializes
 */
export function initializePopupRenderer() {
    console.log('[popupRenderer] initializePopupRenderer called');
    // Expose the renderer to the global CFN namespace for legacy code
    if (typeof window !== 'undefined') {
        window.CFN = window.CFN || {};
        window.CFN.renderModernPopup = renderMapObjectPopup;
        window.CFN.renderMarkerCreationPopup = renderMarkerCreationPopup;
        window.CFN.renderStructureTypeSelector = renderStructureTypeSelector;
        window.CFN.renderStructureForm = renderStructureForm;
        window.CFN.renderUprnDatasetPopup = renderUprnDatasetPopup;
        window.CFN.cleanupModernPopup = cleanupMapObjectPopup;
        // makePopup is the main entry point for creating popups
        window.CFN.makePopup = makeMapPopup;
        // bindFeaturePopup is for GeoJSON layers to use the popup system
        window.CFN.bindFeaturePopup = bindFeaturePopup;
        console.log('[popupRenderer] Functions exposed to window.CFN:', Object.keys(window.CFN));
    }
}
